import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getMetaConnection } from '../../services/metaAdsService';
import { getMetaErrorMessage, isMetaAdsOffResponse, readMetaErrorCode, simulateMetaDisconnected } from '../metaAdsConfig';
import { normalizeMetaConnection } from '../metaAdsConnection';

/**
 * Estado de `GET /getmetaconnection` del espacio activo. Decide si la sección
 * Meta Ads existe para este usuario:
 * - `enabled` falso (flag del build apagado o sin sesión) → oculta, sin request.
 * - 404 sin `code` (flag `META_ADS_ENABLED` apagado en la API) → oculta.
 * - 403 `SPACE_FORBIDDEN` (no es editor del espacio) → oculta.
 * - Otro error → visible, con `error` para reintentar.
 *
 * `refresh` vuelve a pedir sin volver al skeleton. `applyConnectionData` usa el
 * `data` de `POST /connectmeta` (misma forma) sin otro GET.
 *
 * @param {{ ownerUserId?: string|null, enabled?: boolean }} options
 * @returns {{ loaded: boolean, loading: boolean, hidden: boolean, forbidden: boolean,
 *   error: string|null, connection: object|null, refresh: () => Promise<void>,
 *   applyConnectionData: (data: object) => void }}
 */
export function useMetaConnection({ ownerUserId = null, enabled = false } = {}) {
  const [state, setState] = useState({
    loaded: false, loading: false, hidden: !enabled, forbidden: false, error: null, connection: null,
  });
  const controllerRef = useRef(null);
  const owner = ownerUserId ? String(ownerUserId) : null;

  const load = useCallback(async ({ reset = false } = {}) => {
    controllerRef.current?.abort();
    if (!enabled) {
      setState({ loaded: false, loading: false, hidden: true, forbidden: false, error: null, connection: null });
      return;
    }
    const controller = new AbortController();
    controllerRef.current = controller;
    setState((prev) => (reset
      ? { loaded: false, loading: true, hidden: false, forbidden: false, error: null, connection: null }
      : { ...prev, loading: true }));
    let res;
    try {
      res = await getMetaConnection({ ownerUserId: owner, signal: controller.signal });
    } catch (error) {
      if (error?.name === 'AbortError') return;
      throw error;
    }
    if (controller.signal.aborted) return;
    if (res?._httpOk) {
      // `simulateMetaDisconnected`: no-op salvo `.env.local` en localhost (probar el historial).
      const connection = normalizeMetaConnection(simulateMetaDisconnected(res.data));
      setState({ loaded: true, loading: false, hidden: false, forbidden: false, error: null, connection });
      // Ajuste 11: `refresh()` resuelve con la conexión fresca (el chequeo de foco decide con ella).
      return connection;
    }
    const forbidden = res?._httpStatus === 403 && readMetaErrorCode(res) === 'SPACE_FORBIDDEN';
    if (isMetaAdsOffResponse(res) || forbidden) {
      setState({ loaded: true, loading: false, hidden: true, forbidden, error: null, connection: null });
      return;
    }
    setState((prev) => ({
      ...prev, loaded: true, loading: false, hidden: false, forbidden: false, error: getMetaErrorMessage(res),
    }));
  }, [enabled, owner]);

  useEffect(() => {
    load({ reset: true });
    return () => controllerRef.current?.abort();
  }, [load]);

  const refresh = useCallback(() => load(), [load]);

  const applyConnectionData = useCallback((data) => {
    if (!data || typeof data !== 'object') return;
    setState({
      loaded: true, loading: false, hidden: false, forbidden: false, error: null,
      connection: normalizeMetaConnection(data),
    });
  }, []);

  // `ownerUserId`: dueño del espacio ajeno (editor) o `null`; lo usan las rutas de lectura del panel.
  // Memo: MapPage lo pasa por contexto a las fichas (E4); sin memo re-renderizaría todas en cada render.
  return useMemo(
    () => ({ ...state, ownerUserId: owner, refresh, applyConnectionData }),
    [state, owner, refresh, applyConnectionData],
  );
}
